import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Step 2 of signup — exchanges the emailed token for a real tenant.
 * Deliberately does NOT log them in: they sign in once with the
 * credentials they chose, which proves the email and the password both
 * work before they start entering company details.
 */
export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState(token ? "working" : "error");
  const [detail, setDetail] = useState(
    token ? "" : "This link is missing its confirmation code. Please use the link from your email.",
  );
  const [company, setCompany] = useState("");
  // StrictMode double-mounts in dev, which would spend the one-shot token
  // twice and show a spurious "already used" error.
  const fired = useRef(false);

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;
    (async () => {
      try {
        const r = await fetch(`${API}/platform/signup/verify`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });
        let body = {};
        try {
          body = await r.json();
        } catch {
          body = {};
        }
        if (!r.ok) {
          const d = typeof body.detail === "string" ? body.detail.trim() : "";
          setDetail(
            d && d.toLowerCase() !== "internal server error"
              ? d
              : "We couldn't confirm your email. Please try the link again, or sign up afresh.",
          );
          setState("error");
          return;
        }
        setCompany(body?.tenant?.display_name || "");
        setState("done");
      } catch {
        setDetail("Couldn't reach the server. Check your connection and retry.");
        setState("error");
      }
    })();
  }, [token]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-950 px-4"
      data-testid="verify-email-page"
    >
      <Card className="w-full max-w-md border-slate-800 bg-slate-900 text-slate-100">
        {state === "working" && (
          <CardContent className="py-12 text-center" data-testid="verify-working">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-sky-400" />
            <p className="mt-4 text-sm text-slate-300">
              Setting up your company…
            </p>
            <p className="mt-1 text-xs text-slate-500">
              This takes a few seconds. Please don't close this tab.
            </p>
          </CardContent>
        )}

        {state === "done" && (
          <>
            <CardHeader>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/15 mb-3">
                <CheckCircle2 className="h-6 w-6 text-emerald-400" />
              </div>
              <CardTitle className="text-2xl" data-testid="verify-success-title">
                You're all set
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-300">
                {company ? (
                  <>
                    <span className="font-semibold text-slate-100">{company}</span> is
                    created.
                  </>
                ) : (
                  "Your company is created."
                )}{" "}
                Log in with the email and password you just chose, and we'll walk
                you through setting up your rates, trucks and drivers.
              </p>
              <Button asChild className="w-full" data-testid="verify-login-btn">
                <Link to="/login">Log in and finish setup</Link>
              </Button>
            </CardContent>
          </>
        )}

        {state === "error" && (
          <>
            <CardHeader>
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-500/15 mb-3">
                <AlertCircle className="h-6 w-6 text-red-400" />
              </div>
              <CardTitle className="text-2xl" data-testid="verify-error-title">
                We couldn't confirm that
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-300" data-testid="verify-error-detail">
                {detail}
              </p>
              <div className="flex gap-2">
                <Button asChild variant="outline" className="flex-1" data-testid="verify-signup-btn">
                  <Link to="/signup">Sign up again</Link>
                </Button>
                <Button asChild className="flex-1" data-testid="verify-error-login-btn">
                  <Link to="/login">Try logging in</Link>
                </Button>
              </div>
              <p className="text-xs text-slate-500 text-center">
                Already confirmed once? Your company exists — just log in.
              </p>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}
